
import { Box, Dialog, DialogContent, IconButton, TextField } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import CheckboxFormControl from "components/CheckboxFormControl"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { initSparePartTypeDialogData, sparePartTypeColNm } from "./initData"
import { sparePartApiNm } from "./constant"
import { alertConfirmDelete } from "components/Alert"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import { addIdForDataGrid } from "utils"
import AddButton from "components/buttons/AddButton"
import { ArticleRounded } from "resource/muiImport"

let selectedTypeId = null

const sparePartTypeColumns = [
  {
    field: 'ShowDetail',
    headerName: '',
    width: 40,
    align: 'center',
    renderCell: () => (
      <IconButton size="small">
        <ArticleRounded color="primary" />
      </IconButton>
    )
  },
  {
    field: "No",
    headerName: "No.",
    width: 50,
    align: 'center',
  },
  {
    field: "SpTypNm",
    headerName: sparePartTypeColNm.SpTypNm,
    width: 200,
  },
  {
    field: "SpTypSNm",
    headerName: sparePartTypeColNm.SpTypSNm,
    width: 100,
  },
  {
    field: "Dscp",
    headerName: sparePartTypeColNm.Dscp,
    flex: 1,
    minWidth: 120,
  },
]

// Sub dialog for insert/update spare part type
const SparePartTypeSubDialog = ({ dialogOpen, setDialogOpen, selectedId, onFinish }) => {
  const { ax } = useContext(GlobalStateContext)
  const [dialogData, setDialogData] = useState({ ...initSparePartTypeDialogData })

  const dialogDataProp = useCallback((name) => ({
    size: "small",
    label: sparePartTypeColNm[name],
    value: dialogData[name] ?? "",
    onChange: (e) => setDialogData(o => ({ ...o, [name]: e.target.value })),
  }), [dialogData])

  const handleDialogOk = useCallback(() => {
    const apiNm = selectedId ? sparePartApiNm.updateSparePartType : sparePartApiNm.insertSparePartType
    ax.post(apiNm, dialogData).then(value => {
      if (value.data) {
        onFinish()
        setDialogOpen(false)
      }
    })
  }, [ax, dialogData, selectedId, onFinish, setDialogOpen])

  const handleDelete = useCallback(() => {
    alertConfirmDelete(() => {
      ax.post(sparePartApiNm.deleteSparePartType, { SpTypId: selectedId }).then(value => {
        if (value.data) {
          onFinish()
          setDialogOpen(false)
        }
      })
    })
  }, [ax, selectedId, onFinish, setDialogOpen])

  useEffect(() => {
    if (dialogOpen) {
      if (selectedId) {
        ax.post(sparePartApiNm.getSparePartType, { SpTypId: selectedId }).then(value => {
          if (value.data && value.data.length > 0) {
            setDialogData(value.data[0])
          }
        })
      } else {
        setDialogData({ ...initSparePartTypeDialogData })
      }
    }
  }, [dialogOpen, ax, selectedId])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return
        setDialogOpen(false)
      }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='xs'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="ข้อมูลประเภทอะไหล่"
        onDeleteClick={selectedId ? handleDelete : null}
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          <CheckboxFormControl label={"Active"}
            checked={dialogData.IsActv === 1}
            onChange={e => setDialogData(o => ({ ...o, IsActv: e.target.checked ? 1 : 0 }))} />
          <TextField {...dialogDataProp('SpTypNm')} required />
          <TextField {...dialogDataProp('SpTypSNm')} />
          <TextField {...dialogDataProp('Dscp')} multiline rows={2} />
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="บันทึก"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk} />
    </Dialog>
  )
}

// Main dialog showing list of spare part types
const SparePartTypeDialog = ({ dialogOpen, setDialogOpen, onFinish }) => {

  const { ax } = useContext(GlobalStateContext)
  const [dataTable, setDataTable] = useState([])
  const [subDialogOpen, setSubDialogOpen] = useState(false)

  const getData = useCallback(() => {
    ax.post(sparePartApiNm.getSparePartType, {}).then(value => {
      if (value.data) {
        setDataTable(addIdForDataGrid(value.data, 'SpTypId'))
      }
    })
  }, [ax])

  const handleCellClick = useCallback((params) => {
    if (params.field === 'ShowDetail') {
      selectedTypeId = params.id
      setSubDialogOpen(true)
    }
  }, [])

  const handleRowDoubleClick = useCallback((params) => {
    selectedTypeId = params.id
    setSubDialogOpen(true)
  }, [])

  const handleAdd = useCallback(() => {
    selectedTypeId = null
    setSubDialogOpen(true)
  }, [])

  const onSubFinish = useCallback(() => {
    getData()
    onFinish()
  }, [getData, onFinish])

  useEffect(() => {
    if (dialogOpen) {
      getData()
    }
  }, [dialogOpen, getData])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return
        setDialogOpen(false)
      }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='sm'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="จัดการประเภทอะไหล่"
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          <BoxFR>
            <AddButton onClick={handleAdd}>เพิ่มประเภท</AddButton>
          </BoxFR>
          <Box height={400}>
            <DataGridCellExpand
              hideFooter
              rows={dataTable}
              columns={sparePartTypeColumns}
              onRowDoubleClick={handleRowDoubleClick}
              onCellClick={handleCellClick}
            />
          </Box>
        </BoxFC>
      </DialogContent>
      <SparePartTypeSubDialog
        dialogOpen={subDialogOpen}
        setDialogOpen={setSubDialogOpen}
        selectedId={selectedTypeId}
        onFinish={onSubFinish}
      />
    </Dialog>
  )
}

export default React.memo(SparePartTypeDialog)
